import React, { useRef, useState, useEffect } from "react";
import { Tooltip, Form, Input, message, Select, InputNumber, Typography, Row, Col, Button } from "antd";
import { Box, IconButton } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import TableWithLoadingWrapper from "../../../generic/dataGrid";
import BankingEnabledValue from "./bankingEnabledValue";
import { backendPatchWithoutRefresh } from "../../../../helpers/callBackend";
import GenericDropdown from "../../../generic/genericDropdown";
import GenericModal from "../../../buttons/genericModal";
import GenericDeleteModal from "../../../buttons/genericDeleteModal";
import LoadingButton from '@mui/lab/LoadingButton';
import EntityPaymentsApplicationState from "./EntityPaymentsApplicationState";
import checkFlag from "../../../../helpers/featureFlags";
import PermissionGate from "../../../../helpers/PermissionGate";


function HoldingEntityTable(props) {
  const [visible, setVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [ownershipRows, setOwnershipRows] = useState([]);
  const [currentEntity, setCurrentEntity] = useState(null);

  const renameFormRef = useRef(null);
  const isSoftwareCustomer = JSON.parse(localStorage.getItem("isSoftwareCustomer"))

  // No need for separate API call - use existing entity data from props

  const handleEdit = (entityId, values) => {
    setSubmitting(true);
    
    // Build simplified ownership structure
    const ownershipStructure = ownershipRows.map(row => {
      return {
        child_entity_id: entityId,
        parent_entity_id: row.entityId,
        ownership_percentage: row.percentage
      };
    }).filter(owner => owner.parent_entity_id); // Only include rows with selected entities

    const data = {
      entity_id: entityId,
      name: values.entity_name,
      ownership_structure: ownershipStructure
    };

    backendPatchWithoutRefresh("/api/entity/", data)
      .then((res) => {
        // If response contains updated entity data, update the entity in props
        if (res.data && res.data.entity) {
          console.log('ent', res.data);
          props.updateEntityData(entityId, res.data.entity);
        }
        
        setVisible(false);
        setSubmitting(false);
        setOwnershipRows([]);
        setCurrentEntity(null);
      }).catch((err) => {
        console.error(err);
        setSubmitting(false);
      })
  }

  const addOwnershipRow = () => {
    const currentTotal = ownershipRows.reduce((sum, row) => sum + (row.percentage || 0), 0);
    const remainingPercentage = Math.max(100 - currentTotal, 0);
    
    const newRow = {
      id: Date.now(),
      entityId: null,
      percentage: remainingPercentage
    };
    setOwnershipRows([...ownershipRows, newRow]);
  };

  const removeOwnershipRow = (rowId) => {
    setOwnershipRows(ownershipRows.filter(row => row.id !== rowId));
  };

  const updateOwnershipRow = (rowId, field, value) => {
    setOwnershipRows(ownershipRows.map(row => 
      row.id === rowId ? { ...row, [field]: value } : row
    ));
  };

  const getTotalOwnership = () => {
    return ownershipRows.reduce((sum, row) => sum + (row.percentage || 0), 0);
  };

  const isValidOwnership = () => {
    const total = getTotalOwnership();
    return total <= 100;
  };

  const getFilteredEntities = () => {
    return (props.entityData || []).filter(entity => 
      entity.id !== currentEntity?.id
    );
  };

  const handleModalOpen = (entity) => {
    setCurrentEntity(entity);
    setVisible(true);
    // Initialize ownership structure if exists
    if (entity.ownerships && entity.ownerships.length > 0) {
      setOwnershipRows(entity.ownerships.map((owner, index) => ({
        id: Date.now() + index,
        entityId: owner.parent_company,
        percentage: parseFloat(owner.ownership_percentage) || 0
      })));
    } else {
      setOwnershipRows([]);
    }
  };

  const handleModalClose = () => {
    if (renameFormRef.current) {
      renameFormRef.current.resetFields();
    }
    setVisible(false);
    setOwnershipRows([]);
    setCurrentEntity(null);
  };

  const columns = [
    {
      field: "entity_info",
      headerName: "Name",
      flex: 1,
      sortable: true,
      sortComparator: (a, b) => {
        return a?.name?.localeCompare(b?.name);
      },
      renderCell: (params) => (
        <Link
          to={`/holding-entities/details/000${params.row.id}`}
        >
          {params.row.entity_info?.name}
        </Link>
      ),
    },
    {
      field: "bank_account_count",
      headerName: "No. Accounts",
      flex: 0.5,
      type: "number",
      sortable: true,
    },
    {
      field: "banking_enabled",
      headerName: (
        <Box display="flex" alignItems="center">
          Banking Enabled
          <Tooltip title="Arpari bank accounts can be opened for 'Bank Enabled' accounts. Enable by filling out an application.">
            <IconButton size="small" sx={{ ml: 0.5 }}>
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
      flex: 0.5,
      sortable: false,
      renderCell: (params) => (
        <BankingEnabledValue
          isBankingEnabled={params.row.entity_info?.is_complete}
          isInProgress={params.row.entity_info?.in_progress}
          editEntityURL={params.row.entity_info?.edit_application_url}
          newEntityURL={params.row.entity_info?.new_application_url}
        />
      ),
    },
    {
      field: "actions",
      headerName: "",
      flex: 0.1,
      sortable: false,
      align: 'right',
      renderCell: (params) => (
        // <PermissionGate requiredRole='dashboardEditor'>
        <GenericDropdown dropdownItems={[
          <GenericModal
            visible={visible}
            handleCancelModal={handleModalClose}
            showModal={() => handleModalOpen(params.row)}
            size="md"
            button={<a>Edit</a>}
            modalTitle={`Edit ${currentEntity?.entity_info?.name || 'Entity'}`}
            modalBody={
              <Form
                layout="vertical"
                name="basic"
                onFinish={(values) => handleEdit(params.row.id, values)}
                autoComplete="off"
                initialValues={{
                  entity_name: params.row.entity_info?.name,
                  entity_id: params.row.id,
                }}
                ref={renameFormRef}
              >
                {/* Entity Name Section */}
                <Form.Item
                  name="entity_name"
                  label="Entity Name"
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>

                {/* Ownership Structure Section */}
                <div style={{ marginTop: '24px' }}>
                  <Typography.Title level={5} style={{ marginBottom: '8px' }}>
                    Ownership Structure
                  </Typography.Title>
                  <Typography.Text type="secondary" style={{ fontSize: '14px', display: 'block', marginBottom: '16px' }}>
                    If the entity is owned by parent entities, log them here with the ownership percentage of this entity.
                  </Typography.Text>

                  {/* Existing Owner Rows */}
                  {ownershipRows.map((row, index) => (
                    <Row key={row.id} gutter={[12, 8]} style={{ marginBottom: '12px', alignItems: 'center', width: '100%' }}>
                      <Col span={16} style={{ paddingRight: '8px' }}>
                        <Select
                          placeholder="Entity dropdown"
                          value={row.entityId}
                          onChange={(value) => updateOwnershipRow(row.id, 'entityId', value)}
                          style={{ width: '100%' }}
                          showSearch
                          optionFilterProp="children"
                        >
                          {getFilteredEntities().map(entity => (
                            <Select.Option key={entity.id} value={entity.id}>
                              {entity.entity_info?.name}
                            </Select.Option>
                          ))}
                        </Select>
                      </Col>
                      <Col span={6} style={{ paddingRight: '8px' }}>
                        <InputNumber
                          value={row.percentage}
                          onChange={(value) => updateOwnershipRow(row.id, 'percentage', value)}
                          min={0}
                          max={100}
                          formatter={value => `${value}%`}
                          parser={value => value.replace('%', '')}
                          style={{ width: '100%' }}
                        />
                      </Col>
                      <Col span={2} style={{ display: 'flex', justifyContent: 'center' }}>
                        <Button
                          type="text"
                          icon={<DeleteOutlined />}
                          onClick={() => removeOwnershipRow(row.id)}
                          size="small"
                          style={{ color: '#ff4d4f', padding: '4px' }}
                        />
                      </Col>
                    </Row>
                  ))}

                  {/* Add Owner Button */}
                  <div
                    style={{
                      border: '1px dashed #d9d9d9',
                      borderRadius: '6px',
                      padding: '16px',
                      textAlign: 'center',
                      cursor: 'pointer',
                      marginBottom: '16px',
                      ':hover': { borderColor: '#40a9ff' }
                    }}
                    onClick={addOwnershipRow}
                  >
                    <Button type="dashed" icon={<PlusOutlined />} style={{ border: 'none' }}>
                      Add owner
                    </Button>
                  </div>

                  {/* Validation Error */}
                  {!isValidOwnership() && (
                    <div style={{ color: '#ff4d4f', fontSize: '12px', marginBottom: '16px' }}>
                      Total ownership percentage cannot exceed 100%. Current total: {getTotalOwnership()}%
                    </div>
                  )}
                </div>

                <Form.Item>
                  <LoadingButton
                    htmlType="submit"
                    style={{ margin: "3px", width: "100%" }}
                    loading={submitting}
                    loadingPosition="start"
                    disableElevation
                    fullWidth
                    size="small"
                    type="submit"
                    variant="contained"
                    color="primary"
                    sx={{ fontSize: 14, width: '100%', textTransform: 'none' }}
                    disabled={!isValidOwnership()}
                  >
                    Submit
                  </LoadingButton>
                </Form.Item>
              </Form>
            }
          />,
          <GenericDeleteModal
            type='Entity'
            title={`Are you sure you want to delete ${params.row.entity_info?.name}?`}
            URL="/api/entity/"
            deleteParams={{ entity_id: params.row.id }}
            description={`By clicking Delete below you will no longer have the entity ${params.row.entity_info?.name} on your dashboard. If you do not want to proceed or unsure, please click X on the upper right.`}
            handleDeleteSuccess={() => props.deleteEntity(params.row.id)}
            handleDeleteFailure={() => message.error(`Failed to delete the entity ${params.row.entity_info?.name}`)}
          />
        ]} />
        // </PermissionGate>
      ),
    },
  ];
  
  if (checkFlag('paymentsV2') && isSoftwareCustomer) {
    const paymentsCol = {
      field: "payments_enabled",
      headerName: "Payments Enabled",
      flex: 0.5,
      sortable: false,
      renderCell: (params) => {
        const { id, dwolla_customer_id, dwolla_customer_is_verified } = params.row;
        return (
          <EntityPaymentsApplicationState
            entityId={id}
            isVerified={dwolla_customer_is_verified}
            initialCustomerId={dwolla_customer_id} 
          />
        )
      },
    }
    
    columns.splice(3, 0, paymentsCol)

  }

  return (
    <TableWithLoadingWrapper
      columns={columns}
      dataSource={props?.entityData || []}
      tableLayout="fixed"
      scroll={{
        x: 600,
      }}
      sortModel={[
        {
          field: "entity_info",
          sort: "asc",
        },
      ]}
      style={{ width: "100%" }}
      numerator={props.numerator}
      denominator={props.denominator}
      loading={props.loading}
      checkboxSelection={true}
      onRowSelectionModelChange={(newSelectionIds) => {
        const newSelectionEntities = props?.entityData?.filter((row) => newSelectionIds.includes(row?.id)) || [];
        const selectedEntities = newSelectionEntities.map((row) => {
          return {
            id: row?.id,
            name: row?.entity_info?.name,
            entity: row,
          };
        });
        props.setSelectedRows(selectedEntities);
      }}
      rowKey="id"
    />
  );
}

export default HoldingEntityTable;
